import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { AUTH_RETRY } from '../tokens/auth-http.context';
import { isAuthBootstrapUrl, skipsAuth401Retry } from '../utils/auth-http.util';

/**
 * Attaches `Authorization: Bearer` when an access token is present, except for auth bootstrap URLs.
 * On 401, performs a single shared refresh and retries the request once; refresh failure clears the session.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  const retried = req.context.get(AUTH_RETRY);

  const skipAuthHeader = isAuthBootstrapUrl(req.url);
  const token = auth.getAccessTokenSnapshot();
  const authReq =
    token && !skipAuthHeader
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      if (err.status !== 401 || skipsAuth401Retry(req.url) || retried) {
        return throwError(() => err);
      }
      if (!auth.hasRefreshToken()) {
        auth.clearSessionAndRedirect(router);
        return throwError(() => err);
      }
      return auth.performTokenRefresh().pipe(
        switchMap(() => {
          const newTok = auth.getAccessTokenSnapshot();
          if (!newTok) {
            auth.clearSessionAndRedirect(router);
            return throwError(() => err);
          }
          const retry = req.clone({
            setHeaders: { Authorization: `Bearer ${newTok}` },
            context: req.context.set(AUTH_RETRY, true),
          });
          return next(retry);
        }),
        catchError(() => {
          auth.clearSessionAndRedirect(router);
          return throwError(() => err);
        }),
      );
    }),
  );
};
